import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  List,
  Stack,
  Typography
} from '@mui/material';
import { useFormik } from 'formik';
import _ from 'lodash';
import React from 'react';
import { useMutation } from 'react-fetching-library';
import * as Yup from 'yup';

import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import {
  submitAvailabilityStatusAction
} from '../../../../../services/request';
import { SelectingTable } from './SelectingTable';
import { useSelector } from 'react-redux';
import { formatDate, extractApiErrorMessage } from '../../../../../services/functions';


export const AvailabilityStatusForm = props => {
  const {
    data,
    submitCallback,
    handleClose,
    allStatusChoices,
    allAvailabilityLevels,
    treeObjects
  } = props;
  const treeRef = React.useRef();
  const { season, module } = useSelector(state => state.app);
  const { loading, mutate } = useMutation(submitAvailabilityStatusAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    description: Yup.string().nullable(),
    status: Yup.string().required('To pole nie może być puste'),
    availability_level: Yup.number().required('To pole nie może być puste'),
    date_from: Yup.date().nullable(),
    date_to: Yup.date().nullable(),
    is_enabled: Yup.boolean()
  });

  const initialValues = {
    description: data ? data.description || '' : '',
    status: data ? data.status || 'available' : 'available',
    availability_level: data ? data.availability_level || 4 : 4,
    date_from: data && data.date_from ? new Date(data.date_from) : null,
    date_to: data && data.date_to ? new Date(data.date_to) : null,
    is_enabled: data ? data.is_enabled || false : false
  };

  const handleSubmit = async values => {
    const submitData = { ...values };
    submitData.date_from = formatDate(values.date_from);
    submitData.date_to = formatDate(values.date_to);
    submitData.season = season;
    submitData.module = module;

    if (treeRef.current) {
      submitData.tree = treeRef.current.getTree();
    }

    const { error, payload } = await mutate(submitData, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano status dostępności' : 'Dodano nowy status dostępności', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego statusu dostępności');
      setMessage(extractApiErrorMessage(payload));
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', 'Usunięto status dostępności', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się usunąć statusu dostępności');
      setMessage(extractApiErrorMessage(payload));
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Stack display="flex" flexDirection="column" mt={1} p={1}>
          <Box sx={{ width: '100%', maxWidth: 980, ml: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} mb={2}>
              <Typography variant="subtitle2" sx={{ width: 220, textAlign: 'left' }}>Status</Typography>
              <SmallFormTextField
                sx={{ width: 520, maxWidth: '100%' }}
                select
                id="status"
                name="status"
                options={allStatusChoices}
                value={formik.values.status}
                onChange={formik.handleChange}
              />
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} mb={2}>
              <Typography variant="subtitle2" sx={{ width: 220, textAlign: 'left' }}>Poziom dostępności (1-4)</Typography>
              <SmallFormTextField
                sx={{ width: 520, maxWidth: '100%' }}
                select
                id="availability_level"
                name="availability_level"
                options={allAvailabilityLevels}
                value={formik.values.availability_level}
                onChange={formik.handleChange}
              />
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} mb={2}>
              <Typography variant="subtitle2" sx={{ width: 220, textAlign: 'left' }}>Opis</Typography>
              <SmallFormTextField
                sx={{ width: 520, maxWidth: '100%' }}
                name="description"
                id="description"
                type="text"
                multiline
                rows={4}
                placeholder="opis statusu dostępności"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} mb={2}>
              <Typography variant="subtitle2" sx={{ width: 220, textAlign: 'left' }}>Data od</Typography>
              <SmallFormDateField
                sx={{ width: 520, maxWidth: '100%' }}
                name="date_from"
                value={formik.values.date_from}
                onChange={val => formik.setFieldValue('date_from', val)}
                helperText={formik.touched.date_from && formik.errors.date_from}
              />
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} mb={2}>
              <Typography variant="subtitle2" sx={{ width: 220, textAlign: 'left' }}>Data do</Typography>
              <SmallFormDateField
                sx={{ width: 520, maxWidth: '100%' }}
                name="date_to"
                value={formik.values.date_to}
                onChange={val => formik.setFieldValue('date_to', val)}
                helperText={formik.touched.date_to && formik.errors.date_to}
              />
            </Stack>
          </Box>

          {/* <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Aktywna</Typography>
            <Checkbox
              sx={{ flex: '1 1 90%', justifyContent: 'flex-start' }}
              name="is_enabled"
              id="is_enabled"
              checked={formik.values.is_enabled}
              onChange={formik.handleChange}
            />
          </Stack> */}

          {/* Tree selector for camps, events, resorts */}
          <Box>
            <List disablePadding>
              {!_.isEmpty(treeObjects) ? (
                <SelectingTable
                  data={treeObjects}
                  ref={treeRef}
                  initSelected={data ? data.init_selected_tree : []}
                />
              ) : null}
            </List>
          </Box>
        </Stack>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        {data ? (
          <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
            Usuń
          </Button>
        ) : null}
      </form>
      <ConfirmDialog
        title="Usunąć Status Dostępności?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć ten status dostępności?
      </ConfirmDialog>
    </>
  );
};
